import { useEffect, useMemo } from 'react';

import { Box, Flex, Text } from '@chakra-ui/react';
import { Trans } from '@lingui/macro';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { MdLock } from 'react-icons/md';

import { getStripeElementsOptions, getStripePaymentElementOptions } from '../../../../utils/stripeUtil';
import IconWithText from '../../../generic/IconWithText';
import CheckoutDividerTitle from '../CheckoutDividerTitle';

const SECURE_ICON_PROPS = { w: '14px', h: '14px' };

// The pay button lives outside of this provider, so we expose the stripe api through the ref
const CheckoutStripeCard = ({ stripePromise, clientSecret, brandColors, hasBillingAddress, stripeRef, isDisabled }) => {
  const elementsOptions = useMemo(() => getStripeElementsOptions(clientSecret, brandColors), [clientSecret]);

  return (
    <Box w="full">
      <CheckoutDividerTitle isVisible>
        <Trans>Payment</Trans>
      </CheckoutDividerTitle>

      <Flex justify="center" mb="0.75rem">
        <IconWithText icon={MdLock} iconProps={SECURE_ICON_PROPS}>
          <Text fontSize="xs">
            <Trans>Secure payment processed by Stripe</Trans>
          </Text>
        </IconWithText>
      </Flex>

      <Elements stripe={stripePromise} options={elementsOptions}>
        <CheckoutStripeCardFields stripeRef={stripeRef} hasBillingAddress={hasBillingAddress} isDisabled={isDisabled} />
      </Elements>
    </Box>
  );
};

export default CheckoutStripeCard;

const CheckoutStripeCardFields = ({ stripeRef, hasBillingAddress, isDisabled }) => {
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    stripeRef.current.api = stripe && elements ? { stripe, elements } : null;
    return () => {
      stripeRef.current.api = null;
      stripeRef.current.isCardComplete = false;
    };
  }, [stripe, elements]);

  const paymentElementOptions = useMemo(
    () => ({ ...getStripePaymentElementOptions(hasBillingAddress), readOnly: isDisabled }),
    [hasBillingAddress, isDisabled]
  );

  return <PaymentElement options={paymentElementOptions} onChange={event => (stripeRef.current.isCardComplete = event.complete)} />;
};
